/**
 * @file 404 弹弓场景
 * @description 用 requestAnimationFrame 驱动「蓄力 → 弹射 404 → 归位」循环；
 * 皮筋实时跟随弹药；发射时生成主题色粒子拖尾。
 *
 * @whenToUse 仅作为 NotFoundPage 左侧视觉锚点。
 * @whenNotToUse 业务空态、列表无数据；不要复用到常规运营页。
 */

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/cn.ts'

/** 二维点 */
type Point = {
  /** 横坐标（px） */
  x: number
  /** 纵坐标（px） */
  y: number
}

/** 动画阶段 */
type Phase = 'idle' | 'pull' | 'shoot' | 'return' | 'exit'

/** 粒子颜色：取自主题语义色 CSS 变量 */
const PARTICLE_COLORS = [
  'var(--primary)',
  'var(--accent)',
  'var(--warning)',
  'var(--danger)',
] as const

const PHASE_MS = {
  idle: 1500,
  pull: 1000,
  shoot: 1200,
  return: 1500,
} as const

/** 弹药直径 */
const AMMO_SIZE = 96

/** 蓄力拉扯距离 */
const PULL_DISTANCE = 100

type SlingshotSceneProps = {
  /** 外部触发强力弹射（返回首页）时递增 */
  exitNonce: number
  /** 强力弹射闪光结束后回调 */
  onExitComplete: () => void
  className?: string
}

/**
 * 二次贝塞尔插值。
 *
 * @param t - 进度 0~1
 * @param p0 - 起点
 * @param p1 - 控制点
 * @param p2 - 终点
 * @returns 曲线上的点
 */
function quadraticBezier(t: number, p0: Point, p1: Point, p2: Point): Point {
  const u = 1 - t
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  }
}

/**
 * 平滑缓动（ease-in-out）。
 *
 * @param t - 进度 0~1
 * @returns 缓动后进度
 */
function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

/**
 * 线性插值。
 *
 * @param a - 起点
 * @param b - 终点
 * @param t - 进度
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/**
 * 生成带圆角的正多边形 SVG path（viewBox 坐标系，中心在 cx/cy）。
 *
 * @param sides - 边数（≥3）
 * @param cx - 中心 x
 * @param cy - 中心 y
 * @param radius - 外接圆半径
 * @param cornerRadius - 圆角半径
 * @returns SVG path `d`
 */
function roundedPolygonPath(
  sides: number,
  cx: number,
  cy: number,
  radius: number,
  cornerRadius: number,
): string {
  const verts: Point[] = []
  for (let i = 0; i < sides; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / sides
    verts.push({
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    })
  }

  const parts: string[] = []
  for (let i = 0; i < sides; i++) {
    const prev = verts[(i - 1 + sides) % sides]
    const curr = verts[i]
    const next = verts[(i + 1) % sides]
    const toPrev = { x: prev.x - curr.x, y: prev.y - curr.y }
    const toNext = { x: next.x - curr.x, y: next.y - curr.y }
    const lenPrev = Math.hypot(toPrev.x, toPrev.y)
    const lenNext = Math.hypot(toNext.x, toNext.y)
    const r = Math.min(cornerRadius, lenPrev / 2, lenNext / 2)
    const p1 = {
      x: curr.x + (toPrev.x / lenPrev) * r,
      y: curr.y + (toPrev.y / lenPrev) * r,
    }
    const p2 = {
      x: curr.x + (toNext.x / lenNext) * r,
      y: curr.y + (toNext.y / lenNext) * r,
    }
    if (i === 0) parts.push(`M ${p1.x} ${p1.y}`)
    else parts.push(`L ${p1.x} ${p1.y}`)
    parts.push(`Q ${curr.x} ${curr.y} ${p2.x} ${p2.y}`)
  }
  parts.push('Z')
  return parts.join(' ')
}

/** 弹药：圆角正六边形 path（viewBox 0 0 100 100） */
const AMMO_HEX_PATH = roundedPolygonPath(6, 50, 50, 46, 12)

type Particle = {
  el: HTMLDivElement
  bornAt: number
  lifeMs: number
  start: Point
  end: Point
  size: number
  spin: number
}

type EngineRefs = {
  exitNonce: number
  pendingExit: number
  onExitComplete: () => void
}

/**
 * 弹弓 + 404 弹药动画场景（DOM 直写 + rAF，避免每帧 React 重渲染）。
 */
export function SlingshotScene({
  exitNonce,
  onExitComplete,
  className,
}: SlingshotSceneProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const woodRef = useRef<SVGPathElement>(null)
  const ammoRef = useRef<HTMLDivElement>(null)
  const glowRef = useRef<HTMLDivElement>(null)
  const bandLRef = useRef<SVGLineElement>(null)
  const bandRRef = useRef<SVGLineElement>(null)
  const particlesRef = useRef<HTMLDivElement>(null)
  const flashRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<EngineRefs>({
    exitNonce: 0,
    pendingExit: 0,
    onExitComplete,
  })

  engineRef.current.onExitComplete = onExitComplete
  engineRef.current.pendingExit = exitNonce

  useEffect(() => {
    const root = rootRef.current
    const wood = woodRef.current
    const ammo = ammoRef.current
    const glow = glowRef.current
    const bandL = bandLRef.current
    const bandR = bandRRef.current
    const particlesHost = particlesRef.current
    const flash = flashRef.current
    if (
      !root ||
      !wood ||
      !ammo ||
      !glow ||
      !bandL ||
      !bandR ||
      !particlesHost ||
      !flash
    ) {
      return
    }

    const rootEl: HTMLDivElement = root
    const woodEl: SVGPathElement = wood
    const ammoEl: HTMLDivElement = ammo
    const glowEl: HTMLDivElement = glow
    const bandLEl: SVGLineElement = bandL
    const bandREl: SVGLineElement = bandR
    const particlesEl: HTMLDivElement = particlesHost
    const flashEl: HTMLDivElement = flash

    let raf = 0
    let lastTs = 0
    let phase: Phase = 'idle'
    let phaseElapsed = 0
    let rotation = 0
    let particles: Particle[] = []
    let exitFlashDone = false
    let exitStartedAt = 0
    let exitOrigin: Point = { x: 0, y: 0 }

    let p0: Point = { x: 0, y: 0 }
    let tipL: Point = { x: 0, y: 0 }
    let tipR: Point = { x: 0, y: 0 }
    let forkScale = 1

    /**
     * 按视口重算弹弓原点、叉口与木质 Y 架。
     */
    function layout() {
      const w = rootEl.clientWidth
      const h = rootEl.clientHeight
      forkScale = Math.min(1, Math.max(0.55, w / 1100))
      p0 = {
        x: w * 0.12 + 36 * forkScale,
        y: h * 0.5,
      }
      const tipSpread = 30 * forkScale
      const tipUp = 38 * forkScale
      tipL = { x: p0.x - tipSpread, y: p0.y - tipUp }
      tipR = { x: p0.x + tipSpread, y: p0.y - tipUp }
      const handleEnd = { x: p0.x, y: p0.y + 52 * forkScale }
      woodEl.setAttribute(
        'd',
        `M ${tipL.x} ${tipL.y} L ${p0.x} ${p0.y} L ${tipR.x} ${tipR.y} M ${p0.x} ${p0.y} L ${handleEnd.x} ${handleEnd.y}`,
      )
      woodEl.setAttribute('stroke-width', String(10 * forkScale))
    }

    /**
     * 飞行路径关键点。
     */
    function pathPoints(): { p1: Point; p2: Point; returnCtrl: Point } {
      const w = rootEl.clientWidth
      const h = rootEl.clientHeight
      return {
        p1: { x: w * 0.6, y: h * 0.1 },
        p2: { x: w + 100, y: h * 0.3 },
        returnCtrl: { x: w * 0.55, y: h * 0.55 },
      }
    }

    /**
     * 更新皮筋端点：叉口 → 目标点。
     *
     * @param target - 弹药中心（或回弹静息点）
     * @param strokeScale - 线宽缩放（拉伸变细）
     */
    function setBands(target: Point, strokeScale: number) {
      bandLEl.setAttribute('x1', String(tipL.x))
      bandLEl.setAttribute('y1', String(tipL.y))
      bandLEl.setAttribute('x2', String(target.x))
      bandLEl.setAttribute('y2', String(target.y))
      bandREl.setAttribute('x1', String(tipR.x))
      bandREl.setAttribute('y1', String(tipR.y))
      bandREl.setAttribute('x2', String(target.x))
      bandREl.setAttribute('y2', String(target.y))
      const width = Math.max(1.5, 3.5 * strokeScale * forkScale)
      bandLEl.setAttribute('stroke-width', String(width))
      bandREl.setAttribute('stroke-width', String(width))
    }

    /**
     * 写弹药 transform / 光晕。
     *
     * @param pos - 中心点
     * @param sx - scaleX
     * @param sy - scaleY
     * @param rot - 旋转角度
     * @param flying - 是否飞行中（增强光晕）
     */
    function setAmmo(
      pos: Point,
      sx: number,
      sy: number,
      rot: number,
      flying: boolean,
    ) {
      const size = AMMO_SIZE * forkScale
      const half = size / 2
      ammoEl.style.width = `${size}px`
      ammoEl.style.height = `${size}px`
      ammoEl.style.transform = `translate(${pos.x - half}px, ${pos.y - half}px) rotate(${rot}deg) scale(${sx}, ${sy})`
      const glowSize = size * 1.7
      glowEl.style.width = `${glowSize}px`
      glowEl.style.height = `${glowSize}px`
      glowEl.style.transform = `translate(${pos.x - glowSize / 2}px, ${pos.y - glowSize / 2}px) scale(${flying ? 1.2 : 0.8})`
      glowEl.style.opacity = flying ? '0.5' : '0.2'
    }

    /**
     * 发射瞬间生成拖尾粒子。
     *
     * @param origin - 出生点
     * @param now - 当前时间戳
     */
    function spawnParticles(origin: Point, now: number) {
      for (let i = 0; i < 8; i++) {
        const el = document.createElement('div')
        const size = (6 + Math.random() * 10) * forkScale
        const color =
          PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)]
        const ox = (Math.random() - 0.5) * 60
        const oy = (Math.random() - 0.5) * 60
        el.style.cssText = [
          'position:absolute',
          'left:0',
          'top:0',
          'border-radius:9999px',
          `width:${size}px`,
          `height:${size}px`,
          `background:${color}`,
          'pointer-events:none',
          'will-change:transform,opacity',
        ].join(';')
        particlesEl.appendChild(el)
        particles.push({
          el,
          bornAt: now,
          lifeMs: 700 + Math.random() * 400,
          start: { ...origin },
          end: { x: origin.x + ox + 80, y: origin.y + oy - 40 },
          size,
          spin: (Math.random() - 0.5) * 720,
        })
      }
    }

    /**
     * 更新粒子池；生命周期结束则 remove。
     *
     * @param now - 当前时间戳
     */
    function tickParticles(now: number) {
      const next: Particle[] = []
      for (const p of particles) {
        const t = (now - p.bornAt) / p.lifeMs
        if (t >= 1) {
          p.el.remove()
          continue
        }
        const x = lerp(p.start.x, p.end.x, t)
        const y = lerp(p.start.y, p.end.y, t)
        const opacity = 1 - t
        const scale = 1 - t * 0.85
        p.el.style.opacity = String(opacity)
        p.el.style.transform = `translate(${x - p.size / 2}px, ${y - p.size / 2}px) rotate(${p.spin * t}deg) scale(${scale})`
        next.push(p)
      }
      particles = next
    }

    /**
     * 清空粒子。
     */
    function clearParticles() {
      for (const p of particles) p.el.remove()
      particles = []
    }

    /**
     * 进入强力弹射阶段。
     *
     * @param now - 时间戳
     * @param from - 当前弹药位置
     */
    function beginExit(now: number, from: Point) {
      phase = 'exit'
      phaseElapsed = 0
      exitStartedAt = now
      exitFlashDone = false
      exitOrigin = { ...from }
      flashEl.style.opacity = '0'
      clearParticles()
      spawnParticles(from, now)
    }

    layout()
    setAmmo(p0, 1, 1, 0, false)
    setBands(p0, 1)

    /** 当前弹药中心（供 exit 中断时使用） */
    let ammoPos: Point = { ...p0 }

    /**
     * 主循环：deltaTime 驱动阶段进度。
     *
     * @param ts - rAF 时间戳
     */
    function frame(ts: number) {
      if (!lastTs) lastTs = ts
      const dt = Math.min(48, ts - lastTs)
      lastTs = ts
      phaseElapsed += dt

      const pending = engineRef.current.pendingExit
      if (pending !== engineRef.current.exitNonce && phase !== 'exit') {
        engineRef.current.exitNonce = pending
        if (pending > 0) beginExit(ts, ammoPos)
      }

      const { p1, p2, returnCtrl } = pathPoints()
      const bandRest = { x: p0.x, y: p0.y - 8 * forkScale }

      if (phase === 'idle') {
        const t = phaseElapsed / PHASE_MS.idle
        const breath = 1 + 0.05 * Math.sin((phaseElapsed / 600) * Math.PI * 2)
        rotation = 0
        ammoPos = { ...p0 }
        setAmmo(ammoPos, breath, breath, 0, false)
        setBands(ammoPos, 1)
        if (t >= 1) {
          phase = 'pull'
          phaseElapsed = 0
        }
      } else if (phase === 'pull') {
        const t = easeInOut(Math.min(1, phaseElapsed / PHASE_MS.pull))
        ammoPos = {
          x: p0.x - PULL_DISTANCE * forkScale * Math.SQRT1_2 * t,
          y: p0.y + PULL_DISTANCE * forkScale * Math.SQRT1_2 * t,
        }
        setAmmo(ammoPos, lerp(1, 0.8, t), lerp(1, 1.2, t), 0, false)
        setBands(ammoPos, lerp(1, 0.55, t))
        if (t >= 1) {
          phase = 'shoot'
          phaseElapsed = 0
          spawnParticles(p0, ts)
        }
      } else if (phase === 'shoot') {
        const t = Math.min(1, phaseElapsed / PHASE_MS.shoot)
        const eased = 1 - (1 - t) ** 3
        ammoPos = quadraticBezier(eased, p0, p1, p2)
        rotation += 900 * (dt / 1000)
        setAmmo(ammoPos, 1, 1, rotation, true)
        const restT = Math.min(1, phaseElapsed / 180)
        const bandTarget = {
          x: lerp(ammoPos.x, bandRest.x, restT),
          y: lerp(ammoPos.y, bandRest.y, restT),
        }
        setBands(bandTarget, lerp(0.55, 1, restT))
        if (t >= 1) {
          phase = 'return'
          phaseElapsed = 0
          rotation = 0
        }
      } else if (phase === 'return') {
        const t = Math.min(1, phaseElapsed / PHASE_MS.return)
        const eased = easeInOut(t)
        ammoPos = quadraticBezier(eased, p2, returnCtrl, p0)
        setAmmo(ammoPos, 1, 1, 0, t < 0.85)
        const reconnect = Math.max(0, (t - 0.7) / 0.3)
        const bandTarget = {
          x: lerp(bandRest.x, ammoPos.x, reconnect),
          y: lerp(bandRest.y, ammoPos.y, reconnect),
        }
        setBands(bandTarget, 1)
        if (t >= 1) {
          phase = 'idle'
          phaseElapsed = 0
          ammoPos = { ...p0 }
          setAmmo(ammoPos, 1, 1, 0, false)
          setBands(ammoPos, 1)
        }
      } else if (phase === 'exit') {
        // 2 倍观感速度，沿右上方 30° 直线飞出
        const speed = 1.4 * forkScale
        const dist = phaseElapsed * speed
        const rad = (-30 * Math.PI) / 180
        ammoPos = {
          x: exitOrigin.x + Math.cos(rad) * dist,
          y: exitOrigin.y + Math.sin(rad) * dist,
        }
        rotation += 1400 * (dt / 1000)
        setAmmo(ammoPos, 1, 1, rotation, true)
        setBands(bandRest, 1)

        const flashT = (ts - exitStartedAt) / 400
        if (flashT < 0.5) {
          flashEl.style.opacity = String(lerp(0, 0.6, flashT / 0.5))
        } else if (flashT < 1) {
          flashEl.style.opacity = String(lerp(0.6, 0, (flashT - 0.5) / 0.5))
        } else {
          flashEl.style.opacity = '0'
          if (!exitFlashDone) {
            exitFlashDone = true
            engineRef.current.onExitComplete()
          }
        }
      }

      tickParticles(ts)
      raf = requestAnimationFrame(frame)
    }

    const onResize = () => {
      layout()
      if (phase === 'idle') {
        ammoPos = { ...p0 }
        setAmmo(ammoPos, 1, 1, 0, false)
        setBands(ammoPos, 1)
      }
    }

    window.addEventListener('resize', onResize)
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      clearParticles()
    }
  }, [])

  return (
    <div
      ref={rootRef}
      className={cn('relative h-full min-h-80 w-full overflow-hidden', className)}
      aria-hidden
    >
      <svg className="pointer-events-none absolute inset-0 size-full overflow-visible">
        <defs>
          <linearGradient id="sling-wood" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--warning)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--warning)" stopOpacity="0.75" />
          </linearGradient>
        </defs>
        <path
          ref={woodRef}
          d=""
          fill="none"
          stroke="url(#sling-wood)"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          ref={bandLRef}
          x1={0}
          y1={0}
          x2={0}
          y2={0}
          stroke="var(--danger)"
          strokeLinecap="round"
          opacity={0.92}
        />
        <line
          ref={bandRRef}
          x1={0}
          y1={0}
          x2={0}
          y2={0}
          stroke="var(--danger)"
          strokeLinecap="round"
          opacity={0.92}
        />
      </svg>

      <div
        ref={glowRef}
        className="pointer-events-none absolute left-0 top-0 rounded-full bg-primary/35 blur-2xl"
      />

      <div
        ref={ammoRef}
        className="pointer-events-none absolute left-0 top-0 will-change-transform"
      >
        <svg
          viewBox="0 0 100 100"
          className="size-full overflow-visible"
          aria-hidden
        >
          <path
            d={AMMO_HEX_PATH}
            fill="var(--primary)"
            stroke="var(--primary)"
            strokeWidth={3}
            strokeLinejoin="round"
          />
          <text
            x="50"
            y="54"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--primary-foreground)"
            fontSize="22"
            fontWeight="700"
            className="select-none"
          >
            404
          </text>
        </svg>
      </div>

      <div ref={particlesRef} className="pointer-events-none absolute inset-0" />

      <div
        ref={flashRef}
        className="pointer-events-none absolute inset-0 bg-raised opacity-0"
      />
    </div>
  )
}
